import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChevronLeft, ChevronRight, ArrowLeft, Sun, Sunset, Info } from "lucide-react";
import { toast } from "sonner";

function ym(date) { return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`; }
function iso(y, m, d) { return `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`; }

export default function AvailabilityManager() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t, user, loading } = useApp();
  const [pool, setPool] = useState(null);
  const [month, setMonth] = useState(() => new Date());
  const [saving, setSaving] = useState({});
  const [halfPct, setHalfPct] = useState(0.6);
  const [savingPct, setSavingPct] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== "owner") { nav("/auth"); return; }
    load();
    // eslint-disable-next-line
  }, [id, user, loading]);

  const load = async () => {
    const { data } = await api.get(`/pools/${id}`);
    setPool(data);
    setHalfPct(data.half_day_price_pct ?? 0.6);
  };

  const days = useMemo(() => {
    if (!pool) return [];
    const y = month.getFullYear(), m = month.getMonth();
    const first = new Date(y, m, 1);
    const lastDay = new Date(y, m + 1, 0).getDate();
    const start = (first.getDay() + 6) % 7; // Monday-first
    const cells = [];
    for (let i = 0; i < start; i++) cells.push(null);
    for (let d = 1; d <= lastDay; d++) cells.push({ y, m, d, date: iso(y, m, d) });
    return cells;
  }, [month, pool]);

  const slotFor = (dateStr) => {
    if (!pool) return { full: true, half: true };
    if ((pool.unavailable_dates || []).includes(dateStr)) return { full: false, half: false, blocked: true };
    return (pool.availability || {})[dateStr] || { full: true, half: true };
  };

  const toggle = async (dateStr, key) => {
    if (saving[dateStr + key]) return;
    setSaving((s) => ({ ...s, [dateStr + key]: true }));
    const cur = slotFor(dateStr);
    const val = !cur[key];
    try {
      await api.patch(`/pools/${id}/availability`, { date: dateStr, [key]: val });
      setPool((p) => ({ ...p, availability: { ...(p.availability || {}), [dateStr]: { ...cur, [key]: val } } }));
    } catch (e) { toast.error("Failed"); }
    finally { setSaving((s) => ({ ...s, [dateStr + key]: false })); }
  };

  const savePct = async () => {
    setSavingPct(true);
    try {
      await api.patch(`/pools/${id}/half-day-price-pct`, { half_day_price_pct: +halfPct });
      toast.success(t("availability.pctSaved"));
      load();
    } catch (e) { toast.error("Failed"); }
    finally { setSavingPct(false); }
  };

  if (!pool) return <div className="max-w-6xl mx-auto p-12">Loading...</div>;

  const monthLabel = month.toLocaleDateString(undefined, { year: "numeric", month: "long" });
  const fullPrice = pool.price_per_day;
  const halfPrice = +(fullPrice * (pool.half_day_price_pct ?? 0.6)).toFixed(2);

  return (
    <div className="max-w-6xl mx-auto px-6 py-12">
      <Link to="/owner" className="text-sm text-muted-foreground hover:text-[#005C7A] inline-flex items-center gap-1 mb-4" data-testid="back-to-owner"><ArrowLeft className="w-3.5 h-3.5" /> {t("owner.title")}</Link>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
        <div>
          <div className="overline text-[#E27B58] mb-2">{t("availability.badge")}</div>
          <h1 className="font-display text-4xl font-bold">{pool.title}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("availability.subtitle")}</p>
        </div>
        <div className="bg-white border border-border rounded-2xl p-5 min-w-[280px]">
          <Label className="text-xs">{t("availability.halfPct")}</Label>
          <div className="flex items-center gap-3 mt-2">
            <Input type="number" min="0.1" max="1" step="0.05" value={halfPct} onChange={(e) => setHalfPct(e.target.value)} data-testid="half-pct" className="w-24" />
            <Button size="sm" onClick={savePct} disabled={savingPct} className="rounded-full bg-[#005C7A] hover:bg-[#004A63]" data-testid="save-half-pct">{savingPct ? "..." : t("availability.save")}</Button>
          </div>
          <div className="text-xs text-muted-foreground mt-2">Full €{fullPrice} · Half €{halfPrice}</div>
        </div>
      </div>

      <div className="bg-white border border-border rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} className="w-9 h-9 rounded-full border border-border hover:bg-muted flex items-center justify-center" data-testid="prev-month"><ChevronLeft className="w-4 h-4" /></button>
          <h2 className="font-display text-xl font-semibold capitalize">{monthLabel}</h2>
          <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} className="w-9 h-9 rounded-full border border-border hover:bg-muted flex items-center justify-center" data-testid="next-month"><ChevronRight className="w-4 h-4" /></button>
        </div>
        <div className="grid grid-cols-7 gap-2 text-xs text-muted-foreground text-center font-semibold mb-2">
          {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => <div key={d}>{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-2">
          {days.map((d, i) => {
            if (!d) return <div key={i} />;
            const s = slotFor(d.date);
            const today = iso(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
            const past = d.date < today;
            return (
              <div key={d.date} className={`border rounded-xl p-2 ${past ? "opacity-40 bg-muted/30" : "bg-white"} ${s.blocked ? "border-red-200" : "border-border"}`} data-testid={`day-${d.date}`}>
                <div className="text-sm font-semibold mb-2 flex items-center justify-between">
                  <span>{d.d}</span>
                  {s.blocked && <span className="text-[9px] text-red-600 font-semibold">BLK</span>}
                </div>
                <div className="space-y-1.5">
                  <label className={`flex items-center gap-1 text-[10px] cursor-pointer ${past || s.blocked ? "pointer-events-none opacity-50" : ""}`}>
                    <Sun className="w-3 h-3 text-[#005C7A]" />
                    <span className="flex-1">Full</span>
                    <Switch checked={!!s.full} onCheckedChange={() => toggle(d.date, "full")} className="scale-75 origin-right" data-testid={`slot-full-${d.date}`} />
                  </label>
                  <label className={`flex items-center gap-1 text-[10px] cursor-pointer ${past || s.blocked ? "pointer-events-none opacity-50" : ""}`}>
                    <Sunset className="w-3 h-3 text-[#E27B58]" />
                    <span className="flex-1">Half</span>
                    <Switch checked={!!s.half} onCheckedChange={() => toggle(d.date, "half")} className="scale-75 origin-right" data-testid={`slot-half-${d.date}`} />
                  </label>
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-4"><Info className="w-3.5 h-3.5" /> {t("availability.legend")}</div>
      </div>
    </div>
  );
}
